import React from 'react';
import { EditableField } from './EditableField';
import { CalendarIcon, CreditCardIcon, HomeIcon, TrendingUpIcon } from 'lucide-react';
export interface HeaderData {
  title: string;
  believedValue: number;
  datePriced: string;
  creditScore: number;
  loanType: string;
  currentRate: string;
  principalInterest: number;
  loanAmount: number;
  mortgageInsurance: number;
  taxesInsurance: number;
  currentTotalPayment: number;
}
interface HeaderProps {
  data: HeaderData;
  onChange: (key: keyof HeaderData, value: any) => void;
}
export function Header({
  data,
  onChange
}: HeaderProps) {
  return <div className="bg-slate-800 text-white py-10 px-6 sm:px-8 lg:px-12 shadow-lg relative overflow-hidden" data-id="element-36">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-slate-700 rounded-full opacity-50 blur-3xl pointer-events-none" data-id="element-37"></div>
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-60 h-60 bg-blue-900 rounded-full opacity-30 blur-3xl pointer-events-none" data-id="element-38"></div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-39">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-10" data-id="element-40">
          <div data-id="element-41">
            <div className="flex items-center gap-3 mb-2" data-id="element-42">
              <h1 className="text-3xl font-bold tracking-tight text-white" data-id="element-43">
                Refinance Options
              </h1>
              <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs font-bold uppercase tracking-wider rounded-full border border-slate-600" data-id="element-44">
                <EditableField value={data.title} type="text" onSave={val => onChange('title', val)} className="text-slate-300 hover:text-white" data-id="element-45" />
              </span>
            </div>
            <p className="text-slate-400 text-lg" data-id="element-46">
              Compare scenarios to find your optimal path forward.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 bg-slate-900/50 p-4 rounded-xl border border-slate-700/50 backdrop-blur-sm" data-id="element-47">
            <div data-id="element-48">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-49">
                <HomeIcon className="w-3.5 h-3.5" data-id="element-50" /> Believed Value
              </p>
              <EditableField value={data.believedValue} type="currency" onSave={val => onChange('believedValue', val)} className="text-xl font-bold text-white" inputClassName="text-xl font-bold" data-id="element-51" />
            </div>
            <div data-id="element-52">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-53">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-54" /> Date Priced
              </p>
              <EditableField value={data.datePriced} type="text" onSave={val => onChange('datePriced', val)} className="text-lg font-medium text-white" data-id="element-55" />
            </div>
            <div data-id="element-56">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-57">
                <TrendingUpIcon className="w-3.5 h-3.5" data-id="element-58" /> Credit Score
              </p>
              <EditableField value={data.creditScore} type="number" decimals={0} onSave={val => onChange('creditScore', val)} className="text-lg font-medium text-white" data-id="element-59" />
            </div>
            <div data-id="element-60">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-61">
                <CreditCardIcon className="w-3.5 h-3.5" data-id="element-62" /> Loan Type
              </p>
              <EditableField value={data.loanType} type="text" onSave={val => onChange('loanType', val)} className="text-lg font-medium text-white" data-id="element-63" />
            </div>
          </div>
        </div>

        {/* Current Financial Situation */}
        <div className="bg-white/10 rounded-2xl p-6 border border-white/10 backdrop-blur-md" data-id="element-64">
          <h2 className="text-sm font-bold text-slate-300 uppercase tracking-widest mb-4" data-id="element-65">
            Current Financial Situation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 items-center" data-id="element-66">
            <div className="lg:col-span-1" data-id="element-67">
              <p className="text-slate-400 text-sm mb-1" data-id="element-68">Current Rate</p>
              <EditableField value={data.currentRate} type="percent" onSave={val => onChange('currentRate', val)} className="text-2xl font-bold text-white" inputClassName="text-2xl font-bold" data-id="element-69" />
            </div>

            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-70">
              <div data-id="element-71">
                <p className="text-slate-400 text-sm mb-1" data-id="element-72">
                  Principal + Interest
                </p>
                <EditableField value={data.principalInterest} type="currency" onSave={val => onChange('principalInterest', val)} className="text-lg font-medium text-white" data-id="element-73" />
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-id="element-74">
                  Loan:{' '}
                  <EditableField value={data.loanAmount} type="currency" onSave={val => onChange('loanAmount', val)} className="text-slate-400 hover:text-white" data-id="element-75" />
                </p>
              </div>
              <div data-id="element-76">
                <p className="text-slate-400 text-sm mb-1" data-id="element-77">
                  Mortgage Insurance
                </p>
                <EditableField value={data.mortgageInsurance} type="currency" onSave={val => onChange('mortgageInsurance', val)} className="text-lg font-medium text-white" data-id="element-78" />
              </div>
              <div data-id="element-79">
                <p className="text-slate-400 text-sm mb-1" data-id="element-80">Taxes/Insurance</p>
                <EditableField value={data.taxesInsurance} type="currency" onSave={val => onChange('taxesInsurance', val)} className="text-lg font-medium text-white" data-id="element-81" />
              </div>
            </div>

            <div className="lg:col-span-1 bg-blue-500/20 rounded-xl p-4 border border-blue-500/30 text-center lg:text-right" data-id="element-82">
              <p className="text-blue-200 text-sm font-medium mb-1" data-id="element-83">
                Current Total Payment
              </p>
              <EditableField value={data.currentTotalPayment} type="currency" onSave={val => onChange('currentTotalPayment', val)} className="text-3xl font-bold text-white" inputClassName="text-3xl font-bold" data-id="element-84" />
              <p className="text-xs text-blue-300 mt-1" data-id="element-85">per month</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}